import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DateTime, TimeZone } from 'timezonecomplete';

import { rgba } from '@/helpers';
import { otherColors } from '@/styles/colors';
import { media } from '@/styles/theme';
import { HOUR_IN_PX, Z_INDEX_CURRENT_TIME_INDICATOR } from '@/config/variables';
import { useTimezone } from '@/hooks';
import { getTimezoneAbbreviation } from '@/helpers/timezone';

interface IProps {
  isOnTimeline?: boolean;
  isTzVisible?: boolean;
}

interface IIndicator {
  hoursFromMidnight: number;
  isTzVisible?: boolean;
}

const calculateHoursFromMidnight = (currentTime: DateTime, isTzVisible?: boolean): number => {
  if (isTzVisible) return currentTime.diff(currentTime.startOfDay()).hours();

  return currentTime.hour() + currentTime.minute() / 60;
};

const REFRESH_INTERVAL = 1000;

const CurrentTimeIndicator = ({ isOnTimeline = false, isTzVisible = false }: IProps): React.ReactElement => {
  const { timezoneName } = useTimezone();
  const [currentTime, setCurrentTime] = useState(DateTime.now(TimeZone.zone(timezoneName)));
  const hoursFromMidnight = calculateHoursFromMidnight(currentTime, isTzVisible);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = DateTime.now(TimeZone.zone(timezoneName));
      if (currentTime.minute() !== now.minute()) {
        setCurrentTime(now);
      }
    }, REFRESH_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [currentTime, timezoneName]);

  if (isOnTimeline) {
    return (
      <TimeLabelPill className="timelabel-pill" hoursFromMidnight={hoursFromMidnight} isTzVisible={isTzVisible}>
        {`${currentTime.format(`h:mmaaa`)} ${
          isTzVisible ? `(${getTimezoneAbbreviation(timezoneName, currentTime.unixUtcMillis())})` : ''
        }`}
      </TimeLabelPill>
    );
  }

  return <Indicator hoursFromMidnight={hoursFromMidnight} />;
};

export { CurrentTimeIndicator };

const Indicator = styled.div<IIndicator>`
  position: absolute;
  top: ${props => props.hoursFromMidnight * HOUR_IN_PX + 76}px;
  left: 0;
  z-index: ${Z_INDEX_CURRENT_TIME_INDICATOR};
  height: 2px;
  width: 100%;
  background-color: ${rgba(otherColors.calendar.currentTime, 0.3)};
  transition: top 0.2s linear;

  .current-day &,
  .dls-day & {
    background-color: ${rgba(otherColors.calendar.currentTime, 0.76)};
  }

  ${media.tablet} {
    top: ${props => props.hoursFromMidnight * HOUR_IN_PX + 146}px;
  }
`;

const TimeLabelPill = styled.div<IIndicator>`
  position: absolute;
  top: ${props => props.hoursFromMidnight * HOUR_IN_PX + 66}px;
  left: 0;
  z-index: 2;
  width: 100%;
  padding: 2px 0;
  background-color: ${otherColors.calendar.currentTime};
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  min-height: 17px;

  transition: left 0.2s linear, top 0.2s linear;

  ${media.tablet} {
    top: ${props => props.hoursFromMidnight * HOUR_IN_PX + 137}px;
  }

  ${({ isTzVisible }) =>
    isTzVisible &&
    css`
      font-size: 10px;
    `}
`;
